import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav.jsx';
import PublicFooter from '../components/PublicFooter.jsx';
import LobsterMascot from '../components/LobsterMascot.jsx';
import PageMeta from '../components/PageMeta.jsx';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';

const LANDING_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Claw-dius',
  url: 'https://www.claw-dius.com',
  description: 'Voice control for your OpenClaw AI assistant via Amazon Alexa.',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.claw-dius.com/?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export default function LandingPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const steps = t('landing.howItWorks.steps');
  const features = t('landing.features.items');

  return (
    <>
      <PageMeta
        title={t('landing.meta.title')}
        description={t('landing.meta.description')}
        path="/"
        jsonLd={LANDING_JSONLD}
      />

      <div className="pub-page">
        <PublicNav />

        <main id="main-content">
        {/* ── Hero ── */}
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-inner">
            <div className="hero-text">
              <div className="hero-badge">{t('landing.hero.badge')}</div>
              <h1 id="hero-title" className="hero-title">
                {t('landing.hero.title1')}<br />
                <span className="hero-accent">{t('landing.hero.title2')}</span> {t('landing.hero.title3')}
              </h1>
              <p className="hero-sub">{t('landing.hero.sub')}</p>
              <div className="hero-actions">
                <Link to={lp('/login')} className="btn btn-primary btn-lg">{t('landing.hero.ctaPrimary')}</Link>
                <Link to={lp('/pricing')} className="btn btn-ghost btn-lg">{t('landing.hero.ctaSecondary')}</Link>
              </div>
              <p className="hero-note">{t('landing.hero.note')}</p>
            </div>
            <div className="hero-mascot" aria-hidden="true">
              <div className="mascot-bubble">
                <span>{t('landing.hero.mascotBubble')}</span>
              </div>
              <LobsterMascot size={220} className="mascot-img" />
            </div>
          </div>
        </section>

        {/* ── ELI5 Explainer ── */}
        <section className="section section-alt" aria-labelledby="eli5-title">
          <div className="section-inner">
            <div className="eli5-grid">
              <div className="eli5-text">
                <div className="section-label">{t('landing.eli5.label')}</div>
                <h2 id="eli5-title" className="section-title">{t('landing.eli5.title')}</h2>
                <p className="section-body">{t('landing.eli5.body1')}</p>
                <p className="section-body">{t('landing.eli5.body2')}</p>
                <p className="section-body">{t('landing.eli5.body3')}</p>
              </div>
              <div className="eli5-diagram" aria-hidden="true">
                <div className="diagram-node diagram-step-1">
                  <div className="diagram-icon">🗣️</div>
                  <div className="diagram-label">{t('landing.eli5.diagramStep1')}</div>
                </div>
                <div className="diagram-arrow diagram-arrow-1">→</div>
                <div className="diagram-node diagram-node-accent diagram-step-2">
                  <div className="diagram-icon">
                    <LobsterMascot size={40} />
                  </div>
                  <div className="diagram-label">{t('landing.eli5.diagramStep2')}</div>
                </div>
                <div className="diagram-arrow diagram-arrow-2">→</div>
                <div className="diagram-node diagram-step-3">
                  <div className="diagram-icon">🤖</div>
                  <div className="diagram-label">{t('landing.eli5.diagramStep3')}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Disclaimer ── */}
        <aside className="disclaimer-banner" role="note">
          <span className="disclaimer-icon" aria-hidden="true">ℹ️</span>
          <span>
            {t('landing.disclaimer.text')} <strong>{t('landing.disclaimer.textStrong')}</strong>{' '}
            {t('landing.disclaimer.textEnd')}
          </span>
        </aside>

        {/* ── How it works ── */}
        <section className="section" aria-labelledby="how-it-works-title">
          <div className="section-inner">
            <div className="section-header">
              <div className="section-label">{t('landing.howItWorks.label')}</div>
              <h2 id="how-it-works-title" className="section-title">{t('landing.howItWorks.title')}</h2>
              <p className="section-sub">{t('landing.howItWorks.sub')}</p>
            </div>
            <div className="steps-grid">
              {Array.isArray(steps) && steps.map((s) => (
                <article key={s.num} className="step-card">
                  <div className="step-num" aria-hidden="true">{s.num}</div>
                  <h3 className="step-title">{s.title}</h3>
                  <p className="step-body">{s.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="section section-alt" aria-labelledby="features-title">
          <div className="section-inner">
            <div className="section-header">
              <div className="section-label">{t('landing.features.label')}</div>
              <h2 id="features-title" className="section-title">{t('landing.features.title')}</h2>
            </div>
            <div className="features-grid">
              {Array.isArray(features) && features.map((f) => (
                <article key={f.title} className="feature-card">
                  <div className="feature-icon" aria-hidden="true">{f.icon}</div>
                  <h3 className="feature-title">{f.title}</h3>
                  <p className="feature-body">{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Pricing teaser ── */}
        <section className="section" aria-labelledby="pricing-teaser-title">
          <div className="section-inner">
            <div className="pricing-teaser">
              <div className="pricing-teaser-text">
                <h2 id="pricing-teaser-title" className="section-title" style={{ margin: 0 }}>{t('landing.pricingTeaser.title')}</h2>
                <p className="section-body" style={{ margin: '0.75rem 0 0' }}>
                  {t('landing.pricingTeaser.body')}
                </p>
              </div>
              <div className="pricing-teaser-price" aria-label={t('landing.pricingTeaser.priceAriaLabel')}>
                <span className="price-new">{t('landing.pricingTeaser.priceNew')}</span>
                <span className="price-period">{t('landing.pricingTeaser.pricePeriod')}</span>
              </div>
              <Link to={lp('/pricing')} className="btn btn-primary btn-lg">{t('landing.pricingTeaser.cta')}</Link>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="section cta-section" aria-labelledby="cta-title">
          <div className="section-inner cta-inner">
            <LobsterMascot size={120} aria-hidden="true" />
            <div>
              <h2 id="cta-title" className="cta-title">{t('landing.cta.title')}</h2>
              <p className="cta-sub">{t('landing.cta.sub')}</p>
              <Link to={lp('/login')} className="btn btn-primary btn-lg">{t('landing.cta.button')}</Link>
            </div>
          </div>
        </section>

        </main>
        <PublicFooter />
      </div>
    </>
  );
}
